import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@workspace/ui/components/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useForm,
} from '@workspace/ui/components/form';
import { Input } from '@workspace/ui/components/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@workspace/ui/components/select';
import { Textarea } from '@workspace/ui/components/textarea';
import type { Country, State } from 'country-state-city';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRight, Globe, Info, PenLine } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { m } from '~/paraglide/messages.js';
import {
  type BusinessProfileData,
  BusinessProfileSchema,
  type OrgType,
} from '../-schemas/onboarding-schema';

async function loadCountryStateCityLib() {
  const { State, Country } = await import('country-state-city');
  return { State, Country };
}

type OrgTypeOption = {
  id: OrgType;
  name: string;
  description: string;
};

const ORG_TYPE_OPTIONS: OrgTypeOption[] = [
  {
    id: 'llc',
    name: 'LLC',
    description: 'Limited Liability Company',
  },
  {
    id: 'corp',
    name: 'Corp',
    description: 'Corporation (C-Corp / S-Corp)',
  },
  {
    id: 'sas',
    name: 'SAS',
    description: 'Sociedad por Acciones Simplificada',
  },
  {
    id: 'sa',
    name: 'SA',
    description: 'Sociedad Anónima',
  },
  {
    id: 'other',
    name: 'Other',
    description: 'Not listed? Type your own',
  },
  {
    id: 'dao',
    name: 'DAO',
    description: 'Decentralized Autonomous Organization',
  },
];

type Props = {
  values: BusinessProfileData;
  onSubmit: (data: BusinessProfileData) => void;
};

function OrgTypeCard({
  option,
  isSelected,
  onClick,
}: {
  option: OrgTypeOption;
  isSelected: boolean;
  onClick: () => void;
}) {
  const Icon = option.id === 'other' ? PenLine : Info;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`group relative flex flex-col items-start gap-1.5 rounded-xl border-2 p-5 text-left transition-all hover:bg-muted/50 focus:outline-none focus:ring-2 focus:ring-primary/20 ${
        isSelected
          ? 'border-primary bg-primary/5'
          : 'border-border hover:border-muted-foreground/30'
      }`}
    >
      <div className="absolute right-3 top-3">
        <Icon className="h-3.5 w-3.5 text-muted-foreground/40" />
      </div>
      <h3 className="text-xl font-bold text-foreground">{option.name}</h3>
      <p className="text-xs text-muted-foreground">{option.description}</p>
    </button>
  );
}

export function BusinessProfileForm({ values, onSubmit }: Props) {
  const [selectedOrgType, setSelectedOrgType] = useState<OrgType | null>(
    (values.orgType as OrgType) || null,
  );
  const [customOrgType, setCustomOrgType] = useState('');
  const [showForm, setShowForm] = useState(!!values.orgType);
  const customInputRef = useRef<HTMLInputElement>(null);

  const [cscLib, setCscLib] = useState<{
    State: typeof State;
    Country: typeof Country;
  } | null>(null);
  const [countries, setCountries] = useState<{ code: string; name: string }[]>(
    [],
  );
  const [countrySearch, setCountrySearch] = useState('');
  const [stateSearch, setStateSearch] = useState('');

  const form = useForm<BusinessProfileData>({
    resolver: zodResolver(BusinessProfileSchema),
    defaultValues: values,
  });

  useEffect(() => {
    loadCountryStateCityLib().then((lib) => {
      setCscLib(lib);
      const allCountries = lib.Country.getAllCountries().map((c) => ({
        code: c.isoCode,
        name: c.name,
      }));
      setCountries(allCountries);
    });
  }, []);

  const country = form.watch('country');

  const filteredCountries = countries.filter((c) =>
    c.name.toLowerCase().includes(countrySearch.toLowerCase()),
  );

  const getStatesByCountry = useCallback(
    (countryCode: string) => {
      return cscLib?.State.getStatesOfCountry(countryCode) || [];
    },
    [cscLib],
  );

  const statesByCountry = getStatesByCountry(country);
  const filteredStates = statesByCountry.filter((s) =>
    s.name.toLowerCase().includes(stateSearch.toLowerCase()),
  );

  const handleOrgTypeSelect = () => {
    if (!selectedOrgType) return;
    if (selectedOrgType === 'other') {
      if (!customOrgType.trim()) return;
      form.setValue('orgType', customOrgType.trim());
    } else {
      form.setValue('orgType', selectedOrgType);
    }
    setShowForm(true);
  };

  const isOtherSelected = selectedOrgType === 'other';
  const canContinue = selectedOrgType
    ? isOtherSelected
      ? customOrgType.trim().length > 0
      : true
    : false;

  if (!showForm) {
    return (
      <div className="w-full max-w-md space-y-6">
        <div className="grid grid-cols-2 gap-3">
          {ORG_TYPE_OPTIONS.filter((o) => o.id !== 'dao').map((option) => (
            <OrgTypeCard
              key={option.id}
              option={option}
              isSelected={selectedOrgType === option.id}
              onClick={() => {
                setSelectedOrgType(option.id);
                if (option.id === 'other') {
                  setTimeout(() => customInputRef.current?.focus(), 50);
                }
              }}
            />
          ))}
        </div>

        {isOtherSelected && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            transition={{ duration: 0.2 }}
          >
            <Input
              ref={customInputRef}
              placeholder={m['onboarding.org_type.other_placeholder']()}
              value={customOrgType}
              onChange={(e) => setCustomOrgType(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && canContinue) {
                  e.preventDefault();
                  handleOrgTypeSelect();
                }
              }}
              className="h-12"
              autoFocus
            />
          </motion.div>
        )}

        <Button
          onClick={handleOrgTypeSelect}
          disabled={!canContinue}
          className="flex w-full items-center justify-center gap-2 py-6 font-semibold transition-all disabled:cursor-not-allowed disabled:opacity-50"
        >
          {m['common.continue']()}
          <ArrowRight className="h-5 w-5" />
        </Button>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t border-border" />
          </div>
          <div className="relative flex justify-center text-xs">
            <span className="bg-background px-3 text-muted-foreground">
              {m['onboarding.org_type.or']()}
            </span>
          </div>
        </div>

        <button
          type="button"
          onClick={() => {
            setSelectedOrgType('dao');
            form.setValue('orgType', 'dao');
            setShowForm(true);
          }}
          className="flex w-full items-center justify-center gap-3 rounded-xl border border-dashed border-border p-4 text-sm text-muted-foreground transition-colors hover:border-muted-foreground/40 hover:text-foreground"
        >
          <Globe className="h-4 w-4" />
          {m['onboarding.org_type.dao_option']()}
        </button>
      </div>
    );
  }

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key="business-form"
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.2 }}
      >
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="w-full max-w-md space-y-5"
          >
            <div className="space-y-4 rounded-xl border border-border p-6">
              <FormField
                control={form.control}
                name="legalName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="gap-0">
                      {m['onboarding.business_profile.company_name']()}
                      <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder={m[
                          'onboarding.business_profile.company_name_placeholder'
                        ]()}
                        type="text"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="whatYourCompanyDoes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {m['onboarding.business_profile.what_company_does']()}
                    </FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder={m[
                          'onboarding.business_profile.what_company_does_placeholder'
                        ]()}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="country"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="gap-0">
                      {m['onboarding.business_profile.country']()}
                      <span className="text-destructive">*</span>
                    </FormLabel>
                    <Select
                      onValueChange={(value) => {
                        field.onChange(value);
                        setCountrySearch('');
                      }}
                      defaultValue={field.value}
                      onOpenChange={(open) => {
                        if (!open) setCountrySearch('');
                      }}
                    >
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue
                            placeholder={m[
                              'onboarding.business_profile.country_placeholder'
                            ]()}
                          />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <div className="px-2 pb-2">
                          <Input
                            placeholder={m[
                              'onboarding.business_profile.search_country'
                            ]()}
                            value={countrySearch}
                            onChange={(e) => setCountrySearch(e.target.value)}
                            className="h-9"
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') e.preventDefault();
                            }}
                          />
                        </div>
                        <div className="max-h-[300px] overflow-y-auto">
                          {filteredCountries.length === 0 ? (
                            <div className="py-6 text-center text-sm text-muted-foreground">
                              {m[
                                'organization.business_address.no_countries_found'
                              ]()}
                            </div>
                          ) : (
                            filteredCountries.map((c) => (
                              <SelectItem key={c.code} value={c.code}>
                                {c.name}
                              </SelectItem>
                            ))
                          )}
                        </div>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="address1"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="gap-0">
                      {m['onboarding.business_profile.address']()}
                      <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="text"
                        placeholder={m[
                          'onboarding.business_profile.address_placeholder'
                        ]()}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex gap-4">
                <div className="flex-1">
                  <FormField
                    control={form.control}
                    name="state"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="gap-0">
                          {m['onboarding.business_profile.state']()}
                          <span className="text-destructive">*</span>
                        </FormLabel>
                        <Select
                          onValueChange={(value) => {
                            field.onChange(value);
                            setStateSearch('');
                          }}
                          defaultValue={field.value}
                          onOpenChange={(open) => {
                            if (!open) setStateSearch('');
                          }}
                        >
                          <FormControl>
                            <SelectTrigger className="w-full">
                              <SelectValue
                                placeholder={m[
                                  'onboarding.business_profile.state_placeholder'
                                ]()}
                              />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <div className="px-2 pb-2">
                              <Input
                                placeholder={m[
                                  'onboarding.business_profile.search_state'
                                ]()}
                                value={stateSearch}
                                onChange={(e) => setStateSearch(e.target.value)}
                                className="h-9"
                                autoFocus
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') e.preventDefault();
                                }}
                              />
                            </div>
                            <div className="max-h-[300px] overflow-y-auto">
                              {filteredStates.length === 0 ? (
                                <div className="py-6 text-center text-sm text-muted-foreground">
                                  {m[
                                    'organization.business_address.no_states_found'
                                  ]()}
                                </div>
                              ) : (
                                filteredStates.map((s) => (
                                  <SelectItem key={s.isoCode} value={s.isoCode}>
                                    {s.name}
                                  </SelectItem>
                                ))
                              )}
                            </div>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="flex-1">
                  <FormField
                    control={form.control}
                    name="postalCode"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="gap-0">
                          {m['onboarding.business_profile.postal_code']()}
                          <span className="text-destructive">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            type="text"
                            placeholder={m[
                              'onboarding.business_profile.postal_code_placeholder'
                            ]()}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            </div>

            <Button
              type="submit"
              className="flex w-full items-center justify-center gap-2 py-6 font-semibold transition-all"
            >
              {m['common.continue']()}
              <ArrowRight className="h-5 w-5" />
            </Button>
          </form>
        </Form>
      </motion.div>
    </AnimatePresence>
  );
}
